/*
 * Copyright (c) 2022. Revo Digital
 * ---
 * Author: gabrielecavallo
 * File: ZoomFeature.ts
 * Project: mle-client-n
 * Committed last: 2022/6/10 @ 1518
 * ---
 * Description:
 */

import {Editor} from '../../observables/Editor';
import {action, computed, makeObservable, observable} from 'mobx';
import {StageFeature} from '../common/StageFeature';

export const ZOOM_FNAME = 'zoom';

/**
 * Zoom feature. Handles the zoom for the work-area. Attaches also event listeners
 * for swipes. Exposes observable **zoom** value, to use into react
 * components.
 *
 * TODO: Implement swipes
 */
export class ZoomFeature extends StageFeature<void> {
  @observable
  _zoomValue: number;
  @observable
  _minZoom: number;
  @observable
  _maxZoom: number;

  constructor(parent: Editor) {
    super(ZOOM_FNAME, parent, true);

    this._zoomValue = 1;
    this._minZoom = 0.20;
    this._maxZoom = 2;

    // Make us observable
    makeObservable(this);
  }

  destroy(): boolean {
    // Remove event listeners from stage
    return false;
  }

  init(): boolean {
    let parent = this;
    this.target.on('wheel', (event) => {
      // Movement on Y
      const deltaY = event.evt.deltaY;

      if (deltaY > 0)
        // Zoom out
        parent.decrementZoom(0.05);
      // Zoom in
      else parent.incrementZoom(0.05);
    });
    // Add event listeners to stage
    return false;
  }

  @action
  public resetZoom() {
    this._zoomValue = 1;
    this.target.scale({ x: 1, y: 1 });
  }

  @action
  public setZoom(value: number) {
    this._zoomValue = value;
    this.target.scale({ y: value, x: value });
  }

  @action
  public decrementZoom(value = 0.2) {
    if(this._zoomValue > this._minZoom) {
      this._zoomValue -= value;
      this.target.scale({ x: this._zoomValue, y: this._zoomValue });
    }
  }

  @action
  public incrementZoom(value = 0.2) {
    if(this._zoomValue <= this._maxZoom) {
      this._zoomValue += value;
      this.target.scale({ x: this._zoomValue, y: this._zoomValue });
    }
  }

  @computed
  public get zoom() {
    return this._zoomValue;
  }

  @computed
  public get minZoom() {
    return this._minZoom;
  }

  @computed
  public get maxZoom() {
    return this._maxZoom;
  }

  @computed
  public get canIncrement() {
    return this._zoomValue + 0.2 <= this._maxZoom;
  }

  @computed
  public get canDecrement() {
    return this._zoomValue > this._minZoom + 0.2;
  }

  toString(): string {
    return `ZoomFeature. Zoom value: ${ this._zoomValue }. Enabled: ${ this.isEnabled() }`;
  }
}